import { useTheme } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, Text, StyleSheet, TextInput } from 'react-native';

/**
 * Paging Component
 * 
 * Props:
 * - items (Array): full list to paginate
 * - itemsPerPage (Number): items per page, default 20
 * - onPageItemsChange (Function): receives sliced items for current page
 * - colors (Object): theme colors (expects colors.buttons_submit)
 * - initialPage (Number): starting page, default 1
 */
const CustomPaging = ({ items = [], itemsPerPage = 20, onPageItemsChange, initialPage = 1, searchable = false
}) => {
    const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [inputPage, setInputPage] = useState('');
    const totalPages = Math.ceil(items.length / itemsPerPage);

    // Slice items for current page and notify parent
    useEffect(() => {
        const startIdx = (currentPage - 1) * itemsPerPage;
        const endIdx = currentPage * itemsPerPage;
        const pagedItems = items.slice(startIdx, endIdx);
        if (onPageItemsChange) {
            onPageItemsChange(pagedItems);
        }
        setInputPage(''); 
    }, [currentPage, items, itemsPerPage, onPageItemsChange]);

    // Helper to add page button
    const addPageButton = (page) => (
        <TouchableOpacity
            key={page}
            onPress={() => setCurrentPage(page)}
            style={[
                styles.pageButton,
                { backgroundColor: page === currentPage ? colors.buttons_submit : 'transparent' },
            ]}
        >
            <Text style={{ color: page === currentPage ? 'white' : colors.buttons_submit, fontWeight: 'bold' }}>
                {page}
            </Text>
        </TouchableOpacity>
    );

    // Handler when user submits input page number
    const onSubmitInputPage = () => {
        let pageNum = parseInt(inputPage, 10);
        if (!isNaN(pageNum)) {
            if (pageNum < 1) pageNum = 1;
            else if (pageNum > totalPages) pageNum = totalPages;
            setCurrentPage(pageNum);
        }
        setInputPage('');
    };

    // Render page numbers compactly
    const renderPageNumbers = () => {
        const pages = [];
        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(addPageButton(i));
            }
        } else {
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(addPageButton(i));
                pages.push(<Text key="dots1" style={[styles.dots, { color: colors.buttons_submit }]}>...</Text>);
                pages.push(addPageButton(totalPages));
            } else if (currentPage >= totalPages - 3) {
                pages.push(addPageButton(1));
                pages.push(<Text key="dots2" style={[styles.dots, { color: colors.buttons_submit }]}>...</Text>);
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(addPageButton(i));
            } else {
                pages.push(addPageButton(1));
                pages.push(<Text key="dots3" style={[styles.dots, { color: colors.buttons_submit }]}>...</Text>);
                pages.push(addPageButton(currentPage - 1));
                if (searchable) {
                    // Replace middle page number with input
                    pages.push(
                        <TextInput
                            key="pageInput"
                            style={[styles.pageInput, { borderColor: colors.buttons_submit, color: colors.buttons_submit }]}
                            keyboardType="numeric"
                            value={inputPage}
                            placeholder={`${currentPage}`}
                            placeholderTextColor={colors.buttons_submit}
                            onChangeText={setInputPage}
                            onSubmitEditing={onSubmitInputPage}
                            onBlur={onSubmitInputPage}
                            returnKeyType="done"
                        />
                    );
                } else {
                    pages.push(addPageButton(currentPage));
                }
                pages.push(addPageButton(currentPage + 1));
                pages.push(<Text key="dots4" style={[styles.dots, { color: colors.buttons_submit }]}>...</Text>);
                pages.push(addPageButton(totalPages));
            }
        }
        return pages;
    };

    return (
        <View style={styles.container}>
            <TouchableOpacity
                onPress={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={[
                    styles.navButton,
                    { backgroundColor: currentPage === 1 ? '#ccc' : colors.buttons_submit },
                ]}
            >
                <Text style={styles.navButtonText}>{'<'}</Text>
            </TouchableOpacity>

            {renderPageNumbers()}

            <TouchableOpacity
                onPress={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={[
                    styles.navButton,
                    { backgroundColor: currentPage === totalPages ? '#ccc' : colors.buttons_submit },
                ]}
            >
                <Text style={styles.navButtonText}>{'>'}</Text>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginVertical: 10,
    },
    pageButton: {
        marginHorizontal: 4,
        padding: 6,
        borderRadius: 4,
    },
    dots: {
        marginHorizontal: 6,
        fontWeight: 'bold',
        fontSize: 16,
    },
    navButton: {
        marginHorizontal: 4,
        padding: 6,
        borderRadius: 4,
    },
    navButtonText: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: 16,
    },
    pageInput: {
        marginHorizontal: 4,
        paddingHorizontal: 6,
        paddingVertical: 4,
        borderWidth: 1,
        borderRadius: 4,
        fontWeight: 'bold',
        fontSize: 16,
        width: 35,
        textAlign: 'center',
    },
});

export default CustomPaging;